// Service de stockage avec Supabase - Architecture propre v3
import { supabase } from './supabaseClient';

// Utilitaires de logging et d'erreur
const logOperation = (operation, table, success, message = '') => {
  // Logs désactivés
};

const handleError = (error, operation, table) => {
  // Gestion silencieuse des erreurs
};

// Fonctions de mapping pour les noms de colonnes (camelCase ↔ minuscules Supabase)
const camelToSnake = (str) => str.replace(/[A-Z]/g, letter => letter.toLowerCase());

// Mapping pour les cas spéciaux (colonnes qui ne suivent pas la convention)
const camelToDbColumnName = (key) => {
  // Transforme camelCase en minuscules pour Supabase
  // (utilisé pour les mappings en cas de changement de schéma Supabase)
  return camelToSnake(key);
};

// Convertir format date JJ/MM/YYYY vers ISO YYYY-MM-DD
const convertDateFormat = (dateStr) => {
  if (!dateStr) return dateStr;
  
  // Si c'est déjà en format ISO (contient des tirets et pas de slashes)
  if (dateStr.includes('-') && !dateStr.includes('/')) {
    return dateStr;
  }
  
  // Convertir JJ/MM/YYYY en YYYY-MM-DD
  if (dateStr.includes('/')) {
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  return dateStr;
};

const mapToSupabase = (data) => {
  if (!data) return data;
  const mapped = {};
  for (const [key, value] of Object.entries(data)) {
    // Convertir les dates
    if (key === 'effectiveDate' || key === 'effectivedate' || key.toLowerCase().includes('date')) {
      const convertedValue = convertDateFormat(value);
      mapped[key.toLowerCase()] = convertedValue;
    } else if (key === 'method') {
      mapped['paymentmethod'] = value;
    } else {
      // Toutes les clés en minuscule pour Supabase
      mapped[key.toLowerCase()] = value;
    }
  }
  return mapped;
};

// Mapping explicite des colonnes connues
const snakeToCamelMap = {
  firstname: 'firstName',
  lastname: 'lastName',
  registrationdate: 'registrationDate',
  registrationnumber: 'registrationNumber',
  cautionpaid: 'cautionPaid',
  // subscriberid: 'subscriberId', // supprimé car doublon
  serialnumber: 'serialNumber',
  installationdate: 'installationDate',
  calibrationdate: 'calibrationDate',
  replacementdate: 'replacementDate',
  initialreading: 'initialReading',
  lastreading: 'lastReading',
  anomalyalert: 'anomalyAlert',
  billingfrequency: 'billingFrequency',
  meterreading: 'meterReading',
  readingdate: 'readingDate',
  readingtype: 'readingType',
  previousreading: 'previousReading',
  isinit: 'isInitial',
  invoicegenerated: 'invoiceGenerated',
  linkedinvoiceid: 'linkedInvoiceId',
  datatype: 'dataType',
  subscribertype: 'subscriberType',
  minvolume: 'minVolume',
  maxvolume: 'maxVolume',
  priceperm3: 'pricePerM3',
  effectivedate: 'effectiveDate',
  // Invoice mappings
  invoicenumber: 'invoiceNumber',
  billingperiod: 'billingPeriod',
  billingdate: 'billingDate',
  duedate: 'dueDate',
  readingstart: 'readingStart',
  readingend: 'readingEnd',
  totalconsumption: 'totalConsumption',
  baseamount: 'baseAmount',
  totalamount: 'totalAmount',
  taxamount: 'taxAmount',
  paidamount: 'paidAmount',
  remainingamount: 'remainingAmount',
  subscribername: 'subscriberName',
  meterid: 'meterId',
  tariffprice: 'tariffPrice',
  taxrate: 'taxRate',
  linkedreadings: 'linkedReadings',
  createdat: 'createdAt',
  updatedat: 'updatedAt',
  // Payment mappings with camelCase columns from DB
  paymentdate: 'paymentDate',
  paymentDate: 'paymentDate',  // Supabase peut retourner camelCase
  invoiceid: 'invoiceId',
  invoiceId: 'invoiceId',  // Supabase peut retourner camelCase
  subscriberid: 'subscriberId',
  subscriberId: 'subscriberId',  // Supabase peut retourner camelCase
  paymentmethod: 'method',
  paymentMethod: 'method'  // Cas où Supabase retourne avec camelCase
};

const mapFromSupabase = (data) => {
  if (!data) return data;
  const mapped = {};
  for (const [key, value] of Object.entries(data)) {
    const camelKey = snakeToCamelMap[key] || key;
    mapped[camelKey] = value;
  }
  return mapped;
};

// Service Supabase
const storageService = {
  // ====== SUBSCRIBERS ======
  getSubscribers: async () => {
    try {
      const { data, error } = await supabase
        .from('subscribers')
        .select('*');
      
      if (error) {
        handleError(error, 'SELECT', 'subscribers');
        return [];
      }
      logOperation('SELECT', 'subscribers', true, `${data?.length || 0} enregistrements`);
      return (data || []).map(mapFromSupabase);
    } catch (error) {
      handleError(error, 'SELECT', 'subscribers');
      return [];
    }
  },

  getSubscriberById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('subscribers')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        handleError(error, 'SELECT', 'subscribers');
        return null;
      }
      return data ? mapFromSupabase(data) : null;
    } catch (error) {
      handleError(error, 'SELECT', 'subscribers');
      return null;
    }
  },

  saveSubscriber: async (subscriber) => {
    try {
      if (!subscriber.id || !subscriber.firstName || !subscriber.lastName) {
        throw new Error('Données subscriber incomplètes: id, firstName, lastName obligatoires');
      }

      // Mapper les données pour Supabase
      const dataToSave = mapToSupabase(subscriber);

      // Vérifier si existe
      const { data: existing, error: checkError } = await supabase
        .from('subscribers')
        .select('id')
        .eq('id', subscriber.id)
        .maybeSingle();

      if (checkError) {
        handleError(checkError, 'CHECK', 'subscribers');
        return { success: false, error: checkError };
      }

      if (existing) {
        // UPDATE
        const { data, error } = await supabase
          .from('subscribers')
          .update(dataToSave)
          .eq('id', subscriber.id)
          .select();
        
        if (error) {
          handleError(error, 'UPDATE', 'subscribers');
          return { success: false, error };
        }
        logOperation('UPDATE', 'subscribers', true);
        return { success: true, data: mapFromSupabase(data[0]) };
      } else {
        // INSERT
        const { data, error } = await supabase
          .from('subscribers')
          .insert([dataToSave])
          .select();
        
        if (error) {
          handleError(error, 'INSERT', 'subscribers');
          return { success: false, error };
        }
        logOperation('INSERT', 'subscribers', true);
        return { success: true, data: mapFromSupabase(data[0]) };
      }
    } catch (error) {
      handleError(error, 'SAVE', 'subscribers');
      return { success: false, error };
    }
  },

  deleteSubscriber: async (id) => {
    try {
      const { error } = await supabase
        .from('subscribers')
        .delete()
        .eq('id', id);
      
      if (error) {
        handleError(error, 'DELETE', 'subscribers');
        return { success: false, error };
      }
      logOperation('DELETE', 'subscribers', true);
      return { success: true };
    } catch (error) {
      handleError(error, 'DELETE', 'subscribers');
      return { success: false, error };
    }
  },

  // ====== METERS ======
  getMeters: async () => {
    try {
      const { data, error } = await supabase
        .from('meters')
        .select('*');
      
      if (error) {
        handleError(error, 'SELECT', 'meters');
        return [];
      }
      return (data || []).map(mapFromSupabase);
    } catch (error) {
      handleError(error, 'SELECT', 'meters');
      return [];
    }
  },

  getMeterById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('meters')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        handleError(error, 'SELECT', 'meters');
        return null;
      }
      return data ? mapFromSupabase(data) : null;
    } catch (error) {
      handleError(error, 'SELECT', 'meters');
      return null;
    }
  },

  getMetersBySubscriber: async (subscriberId) => {
    try {
      const { data, error } = await supabase
        .from('meters')
        .select('*')
        .eq('subscriberid', subscriberId);
      
      if (error) {
        handleError(error, 'SELECT', 'meters');
        return [];
      }
      return (data || []).map(mapFromSupabase);
    } catch (error) {
      handleError(error, 'SELECT', 'meters');
      return [];
    }
  },

  saveMeter: async (meter) => {
    try {
      const dataToSave = mapToSupabase(meter);
      const { data: existing, error: checkError } = await supabase
        .from('meters')
        .select('id')
        .eq('id', meter.id)
        .maybeSingle();

      if (checkError) {
        handleError(checkError, 'CHECK', 'meters');
        return { success: false, error: checkError };
      }

      if (existing) {
        const { data, error } = await supabase
          .from('meters')
          .update(dataToSave)
          .eq('id', meter.id)
          .select();
        
        if (error) {
          handleError(error, 'UPDATE', 'meters');
          return { success: false, error };
        }
        logOperation('UPDATE', 'meters', true);
        return { success: true, data: mapFromSupabase(data[0]) };
      } else {
        const { data, error } = await supabase
          .from('meters')
          .insert([dataToSave])
          .select();
        
        if (error) {
          handleError(error, 'INSERT', 'meters');
          return { success: false, error };
        }
        logOperation('INSERT', 'meters', true);
        return { success: true, data: mapFromSupabase(data[0]) };
      }
    } catch (error) {
      handleError(error, 'SAVE', 'meters');
      return { success: false, error };
    }
  },

  deleteMeter: async (id) => {
    try {
      const { error } = await supabase
        .from('meters')
        .delete()
        .eq('id', id);
      
      if (error) {
        handleError(error, 'DELETE', 'meters');
        return { success: false, error };
      }
      logOperation('DELETE', 'meters', true);
      return { success: true };
    } catch (error) {
      handleError(error, 'DELETE', 'meters');
      return { success: false, error };
    }
  },

  // ====== READINGS ======
  getReadings: async () => {
    try {
      const { data, error } = await supabase
        .from('readings')
        .select('*');
      
      if (error) {
        handleError(error, 'SELECT', 'readings');
        return [];
      }
      return (data || []).map(mapFromSupabase);
    } catch (error) {
      handleError(error, 'SELECT', 'readings');
      return [];
    }
  },

  getReadingsByMeter: async (meterId) => {
    try {
      const { data, error } = await supabase
        .from('readings')
        .select('*')
        .eq('meterid', meterId)
        .order('readingdate', { ascending: false });
      
      if (error) {
        handleError(error, 'SELECT', 'readings');
        return [];
      }
      return (data || []).map(mapFromSupabase);
    } catch (error) {
      handleError(error, 'SELECT', 'readings');
      return [];
    }
  },

  getReadingById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('readings')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        handleError(error, 'SELECT', 'readings');
        return null;
      }
      return data ? mapFromSupabase(data) : null;
    } catch (error) {
      handleError(error, 'SELECT', 'readings');
      return null;
    }
  },

  saveReading: async (reading) => {
    try {
      const dataToSave = mapToSupabase(reading);
      const { data: existing, error: checkError } = await supabase
        .from('readings')
        .select('id')
        .eq('id', reading.id)
        .maybeSingle();

      if (checkError) {
        handleError(checkError, 'CHECK', 'readings');
        return { success: false, error: checkError };
      }

      if (existing) {
        const { data, error } = await supabase
          .from('readings')
          .update(dataToSave)
          .eq('id', reading.id)
          .select();
        
        if (error) {
          handleError(error, 'UPDATE', 'readings');
          return { success: false, error };
        }
        logOperation('UPDATE', 'readings', true);
        return { success: true, data: mapFromSupabase(data[0]) };
      } else {
        const { data, error } = await supabase
          .from('readings')
          .insert([dataToSave])
          .select();
        
        if (error) {
          handleError(error, 'INSERT', 'readings');
          return { success: false, error };
        }
        logOperation('INSERT', 'readings', true);
        return { success: true, data: mapFromSupabase(data[0]) };
      }
    } catch (error) {
      handleError(error, 'SAVE', 'readings');
      return { success: false, error };
    }
  },

  deleteReading: async (id) => {
    try {
      const { error } = await supabase
        .from('readings')
        .delete()
        .eq('id', id);
      
      if (error) {
        handleError(error, 'DELETE', 'readings');
        return { success: false, error };
      }
      logOperation('DELETE', 'readings', true);
      return { success: true };
    } catch (error) {
      handleError(error, 'DELETE', 'readings');
      return { success: false, error };
    }
  },

  // ====== INVOICES ======
  getInvoices: async () => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*');
      
      if (error) {
        handleError(error, 'SELECT', 'invoices');
        return [];
      }
      return (data || []).map(mapFromSupabase);
    } catch (error) {
      handleError(error, 'SELECT', 'invoices');
      return [];
    }
  },

  getInvoiceById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        handleError(error, 'SELECT', 'invoices');
        return null;
      }
      return data ? mapFromSupabase(data) : null;
    } catch (error) {
      handleError(error, 'SELECT', 'invoices');
      return null;
    }
  },

  getInvoicesBySubscriber: async (subscriberId) => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('subscriberid', subscriberId);
      
      if (error) {
        handleError(error, 'SELECT', 'invoices');
        return [];
      }
      return (data || []).map(mapFromSupabase);
    } catch (error) {
      handleError(error, 'SELECT', 'invoices');
      return [];
    }
  },

  saveInvoice: async (invoice) => {
    try {
      const dataToSave = mapToSupabase(invoice);
      const { data: existing, error: checkError } = await supabase
        .from('invoices')
        .select('id')
        .eq('id', invoice.id)
        .maybeSingle();

      if (checkError) {
        handleError(checkError, 'CHECK', 'invoices');
        return { success: false, error: checkError };
      }

      if (existing) {
        const { data, error } = await supabase
          .from('invoices')
          .update(dataToSave)
          .eq('id', invoice.id)
          .select();
        
        if (error) {
          handleError(error, 'UPDATE', 'invoices');
          return { success: false, error };
        }
        logOperation('UPDATE', 'invoices', true);
        return { success: true, data: mapFromSupabase(data[0]) };
      } else {
        const { data, error } = await supabase
          .from('invoices')
          .insert([dataToSave])
          .select();
        
        if (error) {
          handleError(error, 'INSERT', 'invoices');
          return { success: false, error };
        }
        logOperation('INSERT', 'invoices', true);
        return { success: true, data: mapFromSupabase(data[0]) };
      }
    } catch (error) {
      handleError(error, 'SAVE', 'invoices');
      return { success: false, error };
    }
  },

  deleteInvoice: async (id) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', id);
      
      if (error) {
        handleError(error, 'DELETE', 'invoices');
        return { success: false, error };
      }
      logOperation('DELETE', 'invoices', true);
      return { success: true };
    } catch (error) {
      handleError(error, 'DELETE', 'invoices');
      return { success: false, error };
    }
  },

  updateInvoice: async (id, updates) => {
    try {
      const dataToSave = mapToSupabase(updates);
      const { data, error } = await supabase
        .from('invoices')
        .update(dataToSave)
        .eq('id', id)
        .select();
      
      if (error) {
        handleError(error, 'UPDATE', 'invoices');
        return { success: false, error };
      }
      logOperation('UPDATE', 'invoices', true);
      return { success: true, data: mapFromSupabase(data[0]) };
    } catch (error) {
      handleError(error, 'UPDATE', 'invoices');
      return { success: false, error };
    }
  },

  // ====== PAYMENTS ======
  getPayments: async () => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*');
      
      if (error) {
        handleError(error, 'SELECT', 'payments');
        return [];
      }
      return (data || []).map(mapFromSupabase);
    } catch (error) {
      handleError(error, 'SELECT', 'payments');
      return [];
    }
  },

  savePayment: async (payment) => {
    try {
      const dataToSave = mapToSupabase(payment);
      
      const { data, error } = await supabase
        .from('payments')
        .insert([dataToSave])
        .select();
      
      if (error) {
        handleError(error, 'INSERT', 'payments');
        return { success: false, error };
      }
      logOperation('INSERT', 'payments', true);
      return { success: true, data: mapFromSupabase(data[0]) };
    } catch (error) {
      handleError(error, 'INSERT', 'payments');
      return { success: false, error };
    }
  },

  // ====== TARIFFS ======
  getTariffs: async () => {
    try {
      const { data, error } = await supabase
        .from('tariffs')
        .select('*');
      
      if (error) {
        handleError(error, 'SELECT', 'tariffs');
        return [];
      }
      const mapped = (data || []).map(mapFromSupabase);
      logOperation('SELECT', 'tariffs', true, `${data.length} tarif(s)`);
      return mapped;
    } catch (error) {
      handleError(error, 'SELECT', 'tariffs');
      return [];
    }
  },

  saveTariff: async (tariff) => {
    try {
      const dataToSave = mapToSupabase(tariff);
      
      const { data: existing, error: checkError } = await supabase
        .from('tariffs')
        .select('id')
        .eq('id', tariff.id)
        .maybeSingle();

      if (checkError) {
        handleError(checkError, 'CHECK', 'tariffs');
        return { success: false, error: checkError };
      }

      if (existing) {
        const { data, error } = await supabase
          .from('tariffs')
          .update(dataToSave)
          .eq('id', tariff.id)
          .select();
        
        if (error) {
          handleError(error, 'UPDATE', 'tariffs');
          return { success: false, error };
        }
        logOperation('UPDATE', 'tariffs', true);
        return { success: true, data: mapFromSupabase(data[0]) };
      } else {
        const { data, error } = await supabase
          .from('tariffs')
          .insert([dataToSave])
          .select();
        
        if (error) {
          handleError(error, 'INSERT', 'tariffs');
          return { success: false, error };
        }
        logOperation('INSERT', 'tariffs', true);
        return { success: true, data: mapFromSupabase(data[0]) };
      }
    } catch (error) {
      handleError(error, 'SAVE', 'tariffs');
      return { success: false, error };
    }
  },

  deleteTariff: async (id) => {
    try {
      const { error } = await supabase
        .from('tariffs')
        .delete()
        .eq('id', id);
      
      if (error) {
        handleError(error, 'DELETE', 'tariffs');
        return { success: false, error };
      }
      logOperation('DELETE', 'tariffs', true);
      return { success: true };
    } catch (error) {
      handleError(error, 'DELETE', 'tariffs');
      return { success: false, error };
    }
  },

  // ====== SETTINGS ======
  getSettings: async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('*');
      
      if (error) {
        handleError(error, 'SELECT', 'settings');
        return {};
      }
      
      const settings = {};
      (data || []).forEach(s => {
        settings[s.key] = s.datatype === 'number' ? parseFloat(s.value) : s.value;
      });
      return settings;
    } catch (error) {
      handleError(error, 'SELECT', 'settings');
      return {};
    }
  },

  getSetting: async (key, defaultValue = null) => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('value, datatype')
        .eq('key', key)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        handleError(error, 'SELECT', 'settings');
        return defaultValue;
      }
      
      if (data) {
        return data.datatype === 'number' ? parseFloat(data.value) : data.value;
      }
      return defaultValue;
    } catch (error) {
      handleError(error, 'SELECT', 'settings');
      return defaultValue;
    }
  },

  saveSetting: async (key, value) => {
    try {
      const datatype = typeof value === 'number' ? 'number' : 'string';
      const { error } = await supabase
        .from('settings')
        .upsert({ id: key, key, value: String(value), datatype }, { onConflict: 'key' });
      
      if (error) {
        handleError(error, 'UPSERT', 'settings');
        return { success: false, error };
      }
      logOperation('UPSERT', 'settings', true);
      return { success: true };
    } catch (error) {
      handleError(error, 'UPSERT', 'settings');
      return { success: false, error };
    }
  },

  // ====== MAINTENANCE ======
  getMaintenance: async () => {
    try {
      const { data, error } = await supabase
        .from('maintenance')
        .select('*');
      
      if (error) {
        handleError(error, 'SELECT', 'maintenance');
        return [];
      }
      return (data || []).map(mapFromSupabase);
    } catch (error) {
      handleError(error, 'SELECT', 'maintenance');
      return [];
    }
  },

  saveMaintenance: async (maintenance) => {
    try {
      const dataToSave = mapToSupabase(maintenance);
      const { data, error } = await supabase
        .from('maintenance')
        .insert([dataToSave])
        .select();
      
      if (error) {
        handleError(error, 'INSERT', 'maintenance');
        return { success: false, error };
      }
      logOperation('INSERT', 'maintenance', true);
      return { success: true, data: mapFromSupabase(data[0]) };
    } catch (error) {
      handleError(error, 'INSERT', 'maintenance');
      return { success: false, error };
    }
  }
};

export default storageService;
